import { BitkitLink, BitkitTable, BitkitTableCard, rem, useResponsive } from '@bitrise/bitkit-v2';
import { VisuallyHidden } from '@chakra-ui/react/visually-hidden';

import { BuildCacheToolIds, buildCacheTools } from '@/models/BuildCacheTool';
import { BuildToolInvocationStatus, BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import InvocationStatusIcon from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationStatusIcon';
import { formatDateTime, formatDuration, formatPercent } from '@/utils/commonFormats';
import { buildToolInvocationPath } from '@/utils/monolithApiRouteService';

type InvocationGroup = {
  buildTool: BuildCacheToolIds;
  invocations: BuildToolInvocationWithDetails[];
};

type InvocationRowProps = {
  invocation: BuildToolInvocationWithDetails;
  workspaceSlug: string;
  showCommand?: boolean;
  showDuration?: boolean;
};

const InvocationRow = ({ invocation, workspaceSlug, showCommand = true, showDuration = true }: InvocationRowProps) => (
  <BitkitTable.LinkedRow>
    <BitkitTable.LinkedRow.LinkCell textAlign="center">
      <BitkitLink href={buildToolInvocationPath(workspaceSlug, invocation.invocationId, invocation.tool)}>
        <InvocationStatusIcon status={invocation.status} />
        {/* The status icon carries no text, so the row link would otherwise have no accessible name. */}
        <VisuallyHidden>{invocation.shortCommand || invocation.command || invocation.invocationId}</VisuallyHidden>
      </BitkitLink>
    </BitkitTable.LinkedRow.LinkCell>
    {showCommand && (
      <BitkitTable.Cell title={invocation.command} overflowX="hidden" textOverflow="ellipsis" maxWidth={rem(280)}>
        {invocation.shortCommand || invocation.command}
      </BitkitTable.Cell>
    )}
    <BitkitTable.Cell color={invocation.cacheHitRate === null ? 'text/tertiary' : 'inherit'} textAlign="end">
      {invocation.cacheHitRate === null ? 'No cache usage' : formatPercent(invocation.cacheHitRate)}
    </BitkitTable.Cell>
    {showDuration && (
      <BitkitTable.Cell textAlign="end">
        {invocation.status !== BuildToolInvocationStatus.pending ? formatDuration(invocation.duration) : ''}
      </BitkitTable.Cell>
    )}
    <BitkitTable.Cell>{formatDateTime(invocation.startedAt, { hideSeconds: true, hideYear: true })}</BitkitTable.Cell>
  </BitkitTable.LinkedRow>
);

type InvocationGroupTableProps = {
  group: InvocationGroup;
  workspaceSlug: string;
  testIdPrefix: string;
};

const InvocationGroupTable = ({ group, workspaceSlug, testIdPrefix }: InvocationGroupTableProps) => {
  const { isDesktop, isWideDesktop } = useResponsive();

  const showCommand = group.buildTool !== BuildCacheToolIds.ccache;
  const showDuration = group.buildTool !== BuildCacheToolIds.ccache;

  return (
    <BitkitTableCard
      data-testid={`${testIdPrefix}-${group.buildTool}`}
      titleText={`${buildCacheTools[group.buildTool]?.name ?? group.buildTool} invocations`}
    >
      <BitkitTable.ScrollArea>
        <BitkitTable.Root tableLayout={isDesktop || isWideDesktop ? 'fixed' : 'auto'} variant="card" size="md">
          <BitkitTable.Header>
            <BitkitTable.Row>
              <BitkitTable.ColumnHeader textAlign="center" width={rem(92)}>
                Status
              </BitkitTable.ColumnHeader>
              {showCommand && <BitkitTable.ColumnHeader width="100%">Command</BitkitTable.ColumnHeader>}
              <BitkitTable.ColumnHeader textAlign="end" width={showCommand ? rem(140) : '100%'}>
                Cache hit
              </BitkitTable.ColumnHeader>
              {showDuration && (
                <BitkitTable.ColumnHeader textAlign="end" width={rem(140)}>
                  Duration
                </BitkitTable.ColumnHeader>
              )}
              <BitkitTable.ColumnHeader width={rem(178)}>Started at</BitkitTable.ColumnHeader>
            </BitkitTable.Row>
          </BitkitTable.Header>
          <BitkitTable.Body>
            {group.invocations.map((invocation) => (
              <InvocationRow
                key={invocation.invocationId}
                invocation={invocation}
                workspaceSlug={workspaceSlug}
                showCommand={showCommand}
                showDuration={showDuration}
              />
            ))}
          </BitkitTable.Body>
        </BitkitTable.Root>
      </BitkitTable.ScrollArea>
    </BitkitTableCard>
  );
};

export default InvocationGroupTable;
